import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { AuthShell } from "./ForgotPasswordPage";

export default function ResetPasswordPage() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [state, setState] = useState("checking");
  const [error, setError] = useState("");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); setError("Link non valido. Richiedi un nuovo link di reimpostazione."); return; }
    api.get(`/auth/reset-password/check?token=${encodeURIComponent(token)}`).then(() => setState("form"))
      .catch((e) => { setState("invalid"); setError(errMsg(e, "Link non valido.")); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 8) return setError("La password deve avere almeno 8 caratteri");
    if (pwd !== confirm) return setError("Le password non coincidono");
    setSaving(true);
    try { await api.post("/auth/reset-password", { token, new_password: pwd }); setState("done"); }
    catch (err) { setError(errMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <AuthShell>
      <h2 className="font-display text-3xl font-extrabold text-[#0A1128] mb-2">Nuova password</h2>
      {state === "checking" && <Loader2 className="h-6 w-6 animate-spin text-[#0A1128] mt-6" />}
      {state === "invalid" && (
        <div className="mt-4 space-y-5" data-testid="reset-invalid">
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-[#C8102E] flex gap-3"><AlertTriangle className="h-5 w-5 shrink-0" />{error}</div>
          <Link to="/password-dimenticata" className="inline-block font-bold text-[#0A1128] hover:text-[#E8173A]" data-testid="reset-request-new-link">Richiedi un nuovo link</Link>
        </div>
      )}
      {state === "done" && (
        <div className="mt-4 space-y-5" data-testid="reset-success">
          <div className="rounded-xl border border-[#5FB3EA] bg-[#A6DAFC] p-4 text-sm font-semibold text-[#0A1128] flex gap-3"><CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-700" />Password aggiornata. Ora puoi accedere con la nuova password.</div>
          <Link to="/login" className="flex h-12 rounded-full bg-[#0A1128] hover:bg-[#E8173A] text-white font-bold items-center justify-center" data-testid="reset-go-login">Vai al login</Link>
        </div>
      )}
      {state === "form" && (
        <form onSubmit={submit} className="space-y-5 mt-4" data-testid="reset-form">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Nuova password (min. 8 caratteri)</Label>
            <Input type="password" required autoComplete="new-password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="h-12 rounded-xl" data-testid="reset-password-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Conferma password</Label>
            <Input type="password" required autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-12 rounded-xl" data-testid="reset-confirm-input" />
          </div>
          {error && <div className="text-sm font-semibold text-[#C8102E] bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="reset-error">{error}</div>}
          <button type="submit" disabled={saving} className="w-full h-12 rounded-full bg-[#0A1128] hover:bg-[#E8173A] text-white font-bold flex items-center justify-center transition-colors disabled:opacity-70" data-testid="reset-submit-button">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Salva nuova password"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
